import yaml from 'js-yaml';
import { isAllowedDest } from './whitelist';

export type ManifestFile = {
  name: string;
  src: string;
  dest: string | null;
  mode?: string;
  owner?: string;
};
export type ManifestAction = {
  name: string;
  when: 'once' | 'always' | 'on-change';
  trigger?: string[];
  command: string | null;
};
export type ManifestDoc = {
  version: 1;
  files: ManifestFile[];
  actions: ManifestAction[];
};

export function emptyManifest(): ManifestDoc {
  return { version: 1, files: [], actions: [] };
}

export function parseYaml(text: string): { ok: true; doc: ManifestDoc } | { ok: false; error: string } {
  try {
    const doc = yaml.load(text) as Partial<ManifestDoc> | null;
    if (!doc || typeof doc !== 'object') return { ok: false, error: 'manifest must be a YAML object' };
    if (doc.version !== 1) return { ok: false, error: 'manifest version must be 1' };
    if (doc.files !== undefined && !Array.isArray(doc.files)) return { ok: false, error: 'files must be an array' };
    if (doc.actions !== undefined && !Array.isArray(doc.actions)) return { ok: false, error: 'actions must be an array' };
    return {
      ok: true,
      doc: {
        version: 1,
        files: (doc.files || []).map((f) => ({
          name: f.name,
          src: f.src,
          dest: f.dest ?? null,
          mode: f.mode,
          owner: f.owner,
        })),
        actions: (doc.actions || []).map((a) => ({
          name: a.name,
          when: a.when as ManifestAction['when'],
          trigger: a.trigger,
          command: a.command ?? null,
        })),
      },
    };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

const NAME_RE = /^[a-zA-Z0-9_.-]+$/;
const MODE_RE = /^0[0-7]{3}$/;
const OWNER_RE = /^[a-zA-Z0-9_.-]+:[a-zA-Z0-9_.-]+$/;

export function validateManifestDoc(doc: ManifestDoc): string[] {
  const errors: string[] = [];
  const fileNames = new Set<string>();
  const actionNames = new Set<string>();

  doc.files.forEach((f, i) => {
    if (!f.name || !NAME_RE.test(f.name)) errors.push(`files[${i}].name must use letters, numbers, _, ., or -`);
    if (fileNames.has(f.name)) errors.push(`files[${i}].name duplicates another file`);
    fileNames.add(f.name);
    if (!f.src) errors.push(`files[${i}].src is required`);
    else if (f.src.startsWith('/') || f.src.includes('..') || f.src.split('/').some((s) => !s)) {
      errors.push(`files[${i}].src must be a relative path (no leading /, no .., no empty segments)`);
    }
    if (f.dest === null || f.dest === '') errors.push(`files[${i}].dest is required`);
    else if (!isAllowedDest(f.dest)) errors.push(`files[${i}].dest is outside the allowed prefixes`);
    if (f.mode && !MODE_RE.test(f.mode)) errors.push(`files[${i}].mode must look like 0644`);
    if (f.owner && !OWNER_RE.test(f.owner)) errors.push(`files[${i}].owner must look like root:root`);
  });

  doc.actions.forEach((a, i) => {
    if (!a.name || !NAME_RE.test(a.name)) errors.push(`actions[${i}].name must use letters, numbers, _, ., or -`);
    if (actionNames.has(a.name)) errors.push(`actions[${i}].name duplicates another action`);
    actionNames.add(a.name);
    if (!['once', 'always', 'on-change'].includes(a.when)) errors.push(`actions[${i}].when is invalid`);
    if (a.command === null || a.command === '') errors.push(`actions[${i}].command is required`);
    if (a.when === 'on-change') {
      if (!a.trigger || a.trigger.length === 0) {
        errors.push(`actions[${i}].trigger must include at least one file`);
      } else {
        for (const trigger of a.trigger) {
          if (!fileNames.has(trigger)) errors.push(`actions[${i}].trigger references unknown file "${trigger}"`);
        }
      }
    }
  });

  return errors;
}

export function stringifyYaml(doc: ManifestDoc): string {
  const cleaned = {
    version: 1,
    files: doc.files.map((f) => ({
      name: f.name,
      src: f.src,
      ...(f.dest === null ? { dest: null } : { dest: f.dest }),
      ...(f.mode ? { mode: f.mode } : {}),
      ...(f.owner ? { owner: f.owner } : {}),
    })),
    actions: doc.actions.map((a) => ({
      name: a.name,
      when: a.when,
      ...(a.trigger && a.trigger.length ? { trigger: a.trigger } : {}),
      ...(a.command === null ? { command: null } : { command: a.command }),
    })),
  };
  return yaml.dump(cleaned, { lineWidth: 120, noRefs: true });
}
